import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  List,
  Typography,
  Space,
  Button,
  Grid,
} from 'antd';
import {
  EditOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import type { Lead, LeadStatus, LeadSource } from '../../types';
import { DataTable, type AntDColumn } from '../common/DataTable';
import LeadStatusTag from './LeadStatusTag';
import { formatDateLocale } from '../../utils/date';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface LeadsTableProps {
  leads: Lead[];
  onEdit: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
  onView: (lead: Lead) => void;
  onAddOpportunity?: (lead: Lead) => void;
  loading?: boolean;
  pagination?: any;
  onTableChange?: (pagination: any) => void;
}

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  onEdit,
  onView,
  onAddOpportunity,
  loading = false,
  pagination,
  onTableChange,
}) => {
  const { t, i18n } = useTranslation();
  const screens = useBreakpoint();
  const lang = i18n.language || 'en-US';

  const columns: AntDColumn<Lead>[] = [
    {
      id: 'name',
      label: t('sales:leads.table.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (text: string, record: Lead) => (
        <Button type="link" onClick={() => onView?.(record)}>
          {text}
        </Button>
      ),
    },
    {
      id: 'status',
      label: t('sales:leads.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: LeadStatus) => <LeadStatusTag status={status} />,
    },
    {
      id: 'source',
      label: t('sales:leads.table.source'),
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (source: LeadSource) => t(`sales:leads.source.${source.toLowerCase().replace(' ', '_')}`, source),
    },
    {
      id: 'location',
      label: t('sales:leads.table.location'),
      key: 'location',
      width: 200,
      render: (_: any, record: Lead) => (
        <span>
          {record.city}, {record.country}
        </span>
      ),
    },
    {
      id: 'contacts',
      label: t('sales:leads.table.contacts'),
      key: 'contacts',
      width: 150,
      render: (contacts: Lead['contacts']) => (
        <span>
          {contacts.length} {t('common:contacts')}
        </span>
      ),
    },
    {
      id: 'created_at',
      label: t('sales:leads.table.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: Date) => formatDateLocale(date, lang),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_: any, record: Lead) => (
        <Space>
          <Button
            type="link"
            icon={<DollarOutlined />}
            onClick={e => {
              e.stopPropagation();
              onAddOpportunity?.(record);
            }}
          >
            {t('common:addOpportunity')}
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={e => {
              e.stopPropagation();
              onEdit(record);
            }}
          >
            {t('common:edit')}
          </Button>
        </Space>
      ),
    }
  ];

  if (screens.xs) {
    return (
      <List
        grid={{ gutter: 16, xs: 1, sm: 1 }}
        dataSource={leads}
        loading={loading}
        renderItem={(lead: Lead) => (
          <List.Item key={lead.id}>
            <Card
              title={<Text strong>{lead.name}</Text>}
              style={{ width: '100%' }}
              actions={[
                <Button 
                  key="opportunity" 
                  type="link" 
                  icon={<DollarOutlined />} 
                  onClick={() => {
                    onView?.(lead);
                    setTimeout(() => {
                      const tabElement = document.querySelector('[data-key="3"]');
                      if (tabElement) {
                        (tabElement as HTMLElement).click();
                      }
                    }, 100);
                  }}
                >
                  {t('common:addOpportunity')}
                </Button>,
                <Button 
                  key="edit" 
                  type="link" 
                  icon={<EditOutlined />} 
                  onClick={() => onEdit(lead)}
                >
                  {t('common:edit')}
                </Button>
              ]}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Paragraph>
                  <Text strong>{t('sales:leads.table.status')}: </Text>
                  <LeadStatusTag status={lead.status} />
                </Paragraph>
                <Paragraph>
                  <Text strong>{t('sales:leads.table.source')}: </Text>
                  {t(`sales:leads.source.${lead.source.toLowerCase().replace(' ', '_')}`, lead.source)}
                </Paragraph>
                <Paragraph>
                  <Text strong>{t('sales:leads.table.location')}: </Text>
                  {`${lead.city}, ${lead.country}`}
                </Paragraph>
                <Paragraph>
                  <Text strong>{t('sales:leads.table.contacts')}: </Text>
                  {lead.contacts.length} {t('common:contacts')}
                </Paragraph>
                <Paragraph>
                  <Text strong>{t('sales:leads.table.created_at')}: </Text>
                  {formatDateLocale(lead.created_at, lang)}
                </Paragraph>
              </Space>
            </Card>
          </List.Item>
        )}
      />
    );
  }

  return (
    <DataTable<Lead>
      columns={columns}
      data={leads}
      getRowId={(row: Lead) => row.id}
      loading={loading}
      scroll={{ x: 1300 }}
      onView={onView}
      pagination={pagination}
      onChange={onTableChange}
      showViewAction={false}
    />
  );
};

export default LeadsTable;